import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HardDrive, Box, Activity, AlertTriangle, Plus, ArrowRight } from 'lucide-react';
import { usePoolStore } from '@/stores/usePoolStore';
import { useSandboxStore } from '@/stores/useSandboxStore';
import { useImageCatalogStore } from '@/stores/useDiskImageStore';
import { useAuth } from '@/contexts/AuthContext';
import { SandboxCard } from '@/components/SandboxCard';
import { parseCpuCores, parseMemoryGb } from '@/utils/format';

export function HomePage() {
  const { selectedPoolIds, getSelectedPools } = usePoolStore();
  const { fetchSandboxes, getSandboxesByPool } = useSandboxStore();
  const { images, fetchImages } = useImageCatalogStore();
  const { isAdmin } = useAuth();

  const selectedPools = getSelectedPools();

  // Fetch sandboxes and images for all selected pools.
  // Stores deduplicate: if data is fresh (< 30s), the fetch is a no-op.
  useEffect(() => {
    for (const poolId of selectedPoolIds) {
      fetchSandboxes(poolId);
      fetchImages(poolId);
    }
  }, [selectedPoolIds, fetchSandboxes, fetchImages]);

  // Aggregate sandboxes across all selected pools
  const poolSandboxes = selectedPoolIds.flatMap(poolId => getSandboxesByPool(poolId));
  const runningSandboxes = poolSandboxes.filter((sb) => sb.state === 'Running');
  const failedSandboxes = poolSandboxes.filter((sb) => sb.state === 'Failed');

  // Collect unique image IDs across all selected pools
  const allPoolImageIds = new Set(selectedPools.flatMap(p => p.imageIds));
  const availableImageCount = images.filter((img) => allPoolImageIds.has(img.imageId)).length;

  const totalCpu = runningSandboxes.reduce(
    (sum, sb) => sum + parseCpuCores(sb.shape.processorCount),
    0
  );
  const totalMemoryGi = runningSandboxes.reduce(
    (sum, sb) => sum + parseMemoryGb(sb.shape.memorySize),
    0
  );

  const recentSandboxes = [...poolSandboxes]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 4);

  // Subtitle text
  const subtitle = (() => {
    if (selectedPools.length === 0) return 'Select a pool to get started';
    if (selectedPools.length === 1) {
      return `${selectedPools[0].displayName} pool — ${runningSandboxes.length} sandbox${runningSandboxes.length !== 1 ? 'es' : ''} running`;
    }
    return `${selectedPools.length} pools — ${runningSandboxes.length} sandbox${runningSandboxes.length !== 1 ? 'es' : ''} running`;
  })();

  const stats = [
    {
      name: 'Images',
      value: availableImageCount,
      icon: HardDrive,
      accent: 'text-brand-500',
      bg: 'bg-brand-500/10',
      link: '/images',
    },
    {
      name: 'Sandboxes',
      value: poolSandboxes.length,
      icon: Box,
      accent: 'text-violet-500 dark:text-violet-400',
      bg: 'bg-violet-500/10',
      link: '/sandboxes',
    },
    {
      name: 'Running',
      value: runningSandboxes.length,
      icon: Activity,
      accent: 'text-green-600 dark:text-green-400',
      bg: 'bg-green-500/10',
      link: '/sandboxes',
    },
    {
      name: 'Failed',
      value: failedSandboxes.length,
      icon: AlertTriangle,
      accent: 'text-red-500 dark:text-red-400',
      bg: 'bg-red-500/10',
      link: '/sandboxes',
    },
  ];

  return (
    <div className="p-6 lg:p-8">
      {/* Hero section */}
      <div className="mb-8 animate-stagger-1">
        <h1 className="text-2xl font-semibold text-surface-900 dark:text-surface-100">
          Welcome back
        </h1>
        <p className="text-sm text-surface-500 dark:text-surface-400 mt-1">
          {subtitle}
        </p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8 animate-stagger-2">
        {stats.map((stat) => (
          <Link
            key={stat.name}
            to={stat.link}
            className="card card-hover p-4 group"
          >
            <div className="flex items-center gap-3">
              <div className={`${stat.bg} p-2 rounded-fluent-md`}>
                <stat.icon className={`w-4 h-4 ${stat.accent}`} />
              </div>
              <div>
                <p className="text-2xl font-semibold text-surface-900 dark:text-surface-100 leading-none">
                  {stat.value}
                </p>
                <p className="text-xs text-surface-500 dark:text-surface-400 mt-0.5">
                  {stat.name}
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Resource usage + quick action */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8 animate-stagger-3">
        <div className="lg:col-span-2 card p-5">
          <h2 className="text-sm font-semibold text-surface-900 dark:text-surface-100 mb-4">
            Resource Usage
          </h2>
          <p className="text-xs text-surface-400 dark:text-surface-500 -mt-2 mb-3">
            12 x Standard_D32as_v5 (2 regions x 6 nodes)
          </p>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-surface-500 dark:text-surface-400">CPU Cores</span>
                <span className="font-medium text-surface-900 dark:text-surface-100">
                  {totalCpu} / 384
                </span>
              </div>
              <div className="progress-track">
                <div
                  className="progress-fill-blue"
                  style={{ width: `${Math.min((totalCpu / 384) * 100, 100)}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-surface-500 dark:text-surface-400">Memory</span>
                <span className="font-medium text-surface-900 dark:text-surface-100">
                  {totalMemoryGi.toFixed(1)} GB / 1,536 GB
                </span>
              </div>
              <div className="progress-track">
                <div
                  className="progress-fill-green"
                  style={{ width: `${Math.min((totalMemoryGi / 1536) * 100, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Quick action card */}
        {isAdmin && (
          <Link
            to="/sandboxes"
            className="card card-hover p-5 flex flex-col items-center justify-center text-center group"
          >
            <div className="w-12 h-12 rounded-full bg-brand-500/10 flex items-center justify-center mb-3 group-hover:bg-brand-500/20 transition-colors">
              <Plus className="w-6 h-6 text-brand-500" />
            </div>
            <h3 className="text-sm font-semibold text-surface-900 dark:text-surface-100">
              New Sandbox
            </h3>
            <p className="text-xs text-surface-500 dark:text-surface-400 mt-0.5">
              Create a new sandbox environment
            </p>
          </Link>
        )}
      </div>

      {/* Recent sandboxes as cards */}
      {recentSandboxes.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-surface-900 dark:text-surface-100">
              Recent Sandboxes
            </h2>
            <Link
              to="/sandboxes"
              className="inline-flex items-center gap-1 text-xs text-brand-500 hover:text-brand-400 font-medium transition-colors"
            >
              View all
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {recentSandboxes.map((sandbox) => (
              <SandboxCard key={sandbox.sandboxId} sandbox={sandbox} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
